// EventTypeTree
// ***********************************************************************************************************************
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Box } from "@mui/material";
import * as React from "react";
import { SaveDataObj } from "source/framework/saveData";
import { ToolButton } from "ui/Scripts/source/components/framework/Button/ToolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { IRecordFurtherEducationNode } from "ui/Scripts/source/models/recordFurtherEducation/recordFurtherEducationNode";
import { IRecordFurtherEducationGroupNode, RecordFurtherEducationGroupNode } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordFurtherEducationGroupNode";
import { IRecordFurtherEducationGroupNodes } from "ui/Scripts/source/models/recordFurtherEducationGroup/recordFurtherEducationGroupNodes";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as recordService from "source/models/services/recordService";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { ListHeaderLine } from "source/components/framework/List/ListLine";
import { RecordFurtherEducationDialog } from "../RecordFurtherEducationDialog";
import { RecordFurtherEducationGroupDialog } from "../RecordFurtherEducationGroupDialog";
import { RecordFurtherEducationTreeNode } from "../RecordFurtherEducationTreeNode";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { AddToolButton } from "source/components/framework/Button/AddToolButton";
import { AppAddIcon } from "source/components/framework/Icon/AppAddIcon";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface IRecordFurtherEducationTreeProp {
    classes?: string;
    companyId: string;
    selectedId?: string;
    recordFurtherEducationGroupNodes: IRecordFurtherEducationGroupNodes;
    getRecordFurtherEducationGroupNodes: () => Promise<IRecordFurtherEducationGroupNodes>;
}

export interface IRecordFurtherEducationTreeState {
    // Tree
    items: IRecordFurtherEducationGroupNode[];
    selectedId: string;
    selectedCanBeRemoved: boolean;

    // Dialog
    showEditDialog: boolean;
    showEditGroupDialog: boolean;
    editDialogId: string;
    editDialogParentId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

export class RecordFurtherEducationTree extends React.Component<IRecordFurtherEducationTreeProp, IRecordFurtherEducationTreeState> {
    static getSelectedGroupOrItem = (items: IRecordFurtherEducationGroupNode[], id: string): IRecordFurtherEducationGroupNode | IRecordFurtherEducationNode => {
        if (!id) return null;
        const selectedGroup = items.find(i => i.id === id);
        if (selectedGroup) return selectedGroup;
        return RecordFurtherEducationGroupNode.getRecordFurtherEducationById(items, id);
    };

    static itemCanBeRemoved = (item: IRecordFurtherEducationGroupNode | IRecordFurtherEducationNode): boolean => {
        return item
            ? (RecordFurtherEducationGroupNode.isGroup(item) && item.children.length < 1) || !RecordFurtherEducationGroupNode.isGroup(item)
            : false;
    };

    getStateFromRecordFurtherEducationGroupNodes = (state: IRecordFurtherEducationTreeState, recordFurtherEducationGroupNodes: IRecordFurtherEducationGroupNodes, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        let selected = RecordFurtherEducationTree.getSelectedGroupOrItem(recordFurtherEducationGroupNodes.items, state.selectedId);
        let selectedId = selected ? state.selectedId : "";
        const list = RecordFurtherEducationGroupNode.getAsList(state.items);
        let index = list.findIndex(i => i.id === selectedId);
        if (selected && moveNext && index < list.length - 1) {
            index = index + 1;
            selected = list[index];
            selectedId = selected.id;
        }
        return {
            items: recordFurtherEducationGroupNodes.items,
            selectedId,
            selectedCanBeRemoved: RecordFurtherEducationTree.itemCanBeRemoved(selected),
            editDialogId: setEditDialogData ? (moveNext && selected ? selected.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? index < list.length - 1 : state.editDialogHasNext,
            editDialogParentId: ""
        };
    };

    constructor(props: IRecordFurtherEducationTreeProp) {
        super(props);
        const state = {
            items: [],
            selectedId: props.selectedId ?? "",
            selectedCanBeRemoved: false,
            showEditDialog: false,
            showEditGroupDialog: false,
            editDialogId: "",
            editDialogParentId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false
        };
        const newState = this.getStateFromRecordFurtherEducationGroupNodes(state, props.recordFurtherEducationGroupNodes);
        this.state = {
            ...state,
            ...newState
        };
    }

    searchItems = async (moveNext: boolean = false, setEditDialogData: boolean = true) => {
        const props = this.props;
        const result = await props.getRecordFurtherEducationGroupNodes();
        if (!result) return;
        this.setState(this.getStateFromRecordFurtherEducationGroupNodes(this.state, result, moveNext, setEditDialogData));
    };

    refreshTree = async (setDialogData: boolean = true) => {
        await this.searchItems(false, setDialogData);
    };

    componentDidUpdate(prevProps: IRecordFurtherEducationTreeProp, prevState: IRecordFurtherEducationTreeState) {
        const props = this.props;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId) return;
        this.searchItems();
    }

    handleNodeClick = (id: string) => {
        const state = this.state;
        if (!id) return;
        const items = state.items.map(i => new RecordFurtherEducationGroupNode(i));
        const selected = RecordFurtherEducationTree.getSelectedGroupOrItem(items, id);
        if (!selected) return;
        this.setState({
            selectedId: selected.id,
            selectedCanBeRemoved: RecordFurtherEducationTree.itemCanBeRemoved(selected)
        });
    };

    handleSetCollapsed = (id: string, value: boolean) => {
        const state = this.state;
        if (!id) return;
        const items = state.items.map(i => new RecordFurtherEducationGroupNode(i));
        const selectedGroup = items.find(i => i.id === id);
        if (!selectedGroup) return;
        selectedGroup.collapsed = value;
        this.setState({
            items,
            selectedId: selectedGroup.id
        });
    };

    removeGroup = async (id: string): Promise<ApiSuccess> => {
        if (!id) return;
        // Call server
        const result = await AppUtils.callService(() => recordService.removeRecordFurtherEducationGroup(this.props.companyId, id));
        if (!result) return null;
        this.refreshTree();
        return result;
    };

    getRemoveSaveData = (props: IRecordFurtherEducationTreeProp, educationIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", !props.companyId ? null : props.companyId);
        result.append("educationIds", educationIds);
        return result;
    };

    remove = async (id: string): Promise<ApiSuccess> => {
        const props = this.props;
        if (!id) return;
        const saveData = this.getRemoveSaveData(props, [id]);
        // Call server
        const result = await AppUtils.callService(() => recordService.removeRecordFurtherEducations(saveData.obj));
        if (!result) return null;
        this.refreshTree();
        return result;
    };

    handleRemove = async () => {
        const state = this.state;
        if (!state.selectedId) return;
        const selected = RecordFurtherEducationTree.getSelectedGroupOrItem(state.items, state.selectedId);
        if (!selected) return;
        const confirmationText = RecordFurtherEducationGroupNode.isGroup(selected)
            ? Translations.DoYouReallyWantToRemoveRecordFurtherEducationGroupParameter
            : Translations.DoYouReallyWantToRemoveRecordFurtherEducationParameter;
        if (await AppUtils.showConfirmationDialog(String.format(confirmationText, selected.code), false) === ConfirmationDialogResult.Yes) {
            if (RecordFurtherEducationGroupNode.isGroup(selected)) {
                await this.removeGroup(selected.id);
            } else {
                await this.remove(selected.id);
            }
        }
    };

    handleShowEditDialog = (id: string, groupId: string, isGroup: boolean) => {
        const state = this.state;
        const isNew = id === Base.emptyGuid;
        let list = RecordFurtherEducationGroupNode.getAsList(state.items);
        if (!isGroup) {
            list = list.filter(i => !RecordFurtherEducationGroupNode.isGroup(i));
        } else {
            list = list.filter(i => RecordFurtherEducationGroupNode.isGroup(i));
        }
        const index = isNew ? 0 : list.findIndex(i => i.id === id);
        if (index < 0) return;
        this.setState({
            showEditDialog: !isGroup,
            showEditGroupDialog: isGroup,
            selectedId: id,
            editDialogId: id,
            editDialogParentId: isNew ? groupId : "",
            editDialogHasPrevious: index > 0,
            editDialogHasNext: isNew ? false : index < list.length - 1
        });
    };

    handleEditDialogOk = () => {
        this.refreshTree(false);
    };

    handleEditDialogCancel = (refreshTree: boolean) => {
        this.setState({
            showEditDialog: false,
            showEditGroupDialog: false
        });
        if (!refreshTree) return;
        this.refreshTree(false);
    };

    handleAddGroup = () => {
        this.handleShowEditDialog(Base.emptyGuid, "", true);
    };

    handleAddSub = () => {
        const state = this.state;
        if (!state.selectedId) return;
        const selected = RecordFurtherEducationTree.getSelectedGroupOrItem(state.items, state.selectedId);
        if (!selected) return;
        const groupId = RecordFurtherEducationGroupNode.isGroup(selected)
            ? selected.id
            : selected.groupId;
        this.handleShowEditDialog(Base.emptyGuid, groupId, false);
    };
    
    handleOpen = (id: string) => {
        const state = this.state;
        if (!id) return;
        const selected = RecordFurtherEducationTree.getSelectedGroupOrItem(state.items, id);
        if (!selected) return;
        this.handleShowEditDialog(id, "", RecordFurtherEducationGroupNode.isGroup(selected));
    };

    setEditDialogItemByIndex = (index: number, list: (IRecordFurtherEducationGroupNode | IRecordFurtherEducationNode)[]) => {
        if (index < 0 || index >= list.length) return;
        this.setState({
            selectedId: list[index].id,
            editDialogId: list[index].id,
            editDialogHasPrevious: index > 0,
            editDialogHasNext: index < list.length - 1,
            selectedCanBeRemoved: RecordFurtherEducationTree.itemCanBeRemoved(list[index])
        });
    };

    handleMoveToPrevious = () => {
        const state = this.state;
        if (!state.selectedId) return;
        const selected = RecordFurtherEducationTree.getSelectedGroupOrItem(state.items, state.selectedId);
        if (!selected) return;
        let list = RecordFurtherEducationGroupNode.getAsList(state.items);
        if (state.showEditDialog) {
            list = list.filter(i => !RecordFurtherEducationGroupNode.isGroup(i));
        } else {
            list = list.filter(i => RecordFurtherEducationGroupNode.isGroup(i));
        }
        const index = list.findIndex(i => i.id === selected.id);
        if (index < 0) return;
        this.setEditDialogItemByIndex(index - 1, list);
    };

    handleMoveToNext = () => {
        const state = this.state;
        if (!state.selectedId) return;
        const selected = RecordFurtherEducationTree.getSelectedGroupOrItem(state.items, state.selectedId);
        if (!selected) return;
        let list = RecordFurtherEducationGroupNode.getAsList(state.items);
        if (state.showEditDialog) {
            list = list.filter(i => !RecordFurtherEducationGroupNode.isGroup(i));
        } else {
            list = list.filter(i => RecordFurtherEducationGroupNode.isGroup(i));
        }
        const index = list.findIndex(i => i.id === selected.id);
        if (index < 0) return;
        this.setEditDialogItemByIndex(index + 1, list);
    };

    getInfo = () => {
        const parts = Translations.RecordUserModuleInfoFurtherEducations.split("#");
        return <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <div>{parts[0]}</div>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}><div>{parts[1]}</div><AppAddIcon /><div>{parts[2]}</div><PlaylistAddIcon /><div>{parts[3]}</div></Box>
        </Box>;
    };

    render() {
        const props = this.props;
        const state = this.state;
        const items = state.items;
        return (
            <>
                <DialogSettingsCard
                    fitToContainerHeight
                    title={(props.companyId ? Translations.FurtherEducations : Translations.DefaultFurtherEducations) + " / " + Translations.Competencies}
                    info={props.companyId ? this.getInfo() : undefined}
                    cardContent={
                        <ActionContainer
                            leftItems={
                                <></>
                            }
                            rightItems={
                                <Box sx={{ marginLeft: ".5rem", display: "flex" }}>
                                    <AddToolButton
                                        tooltip={props.companyId ? Translations.AddFurtherEducationGroup + " / " + Translations.CompetencyGroup : Translations.AddDefaultFurtherEducationGroup + " / " + Translations.CompetencyGroup}
                                        onClick={this.handleAddGroup}
                                    />
                                    <ToolButton
                                        variant="icon"
                                        tooltip={props.companyId ? Translations.AddFurtherEducation + " / " + Translations.Competency : Translations.AddDefaultFurtherEducation + " / " + Translations.Competency}
                                        disabled={!state.selectedId}
                                        icon={<PlaylistAddIcon />}
                                        onClick={this.handleAddSub}
                                    />
                                    <RemoveToolButton
                                        disabled={!state.selectedId || !state.selectedCanBeRemoved}
                                        tooltip={props.companyId ? Translations.RemoveFurtherEducation + " / " + Translations.Competency : Translations.RemoveDefaultFurtherEducation + " / " + Translations.Competency}
                                        onClick={this.handleRemove}
                                    />
                                </Box>
                            }
                        />
                    }
                    content={
                        <VerticalContainer
                            before={
                                <ListHeaderLine
                                    columns={[
                                        { size: 5, column: "name", content: Translations.Name },
                                        { size: 5, column: "permission", content: Translations.Description },
                                        { size: 2, column: "permission", content: Translations.RequiredValidQualificationCount },
                                    ]}
                                />
                            }
                            main={
                                <Box sx={{
                                    height: "100%",
                                    overflowX: "hidden",
                                    overflowY: "auto",
                                }}>
                                    {items.map((item) =>
                                        <RecordFurtherEducationTreeNode
                                            key={item.id}
                                            item={item}
                                            level={1}
                                            selectedId={state.selectedId}
                                            onClick={this.handleNodeClick}
                                            onDoubleClick={this.handleOpen}
                                            onSetCollapsed={this.handleSetCollapsed}
                                        />
                                    )}
                                </Box>
                            }
                        />
                    } />
                {state.showEditDialog &&
                    <RecordFurtherEducationDialog
                        id={state.editDialogId}
                        companyId={props.companyId}
                        groupId={state.editDialogParentId}
                        canBeRemoved={state.selectedCanBeRemoved}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onSaved={this.handleEditDialogOk}
                        onClose={this.handleEditDialogCancel}
                        onRemove={this.remove}
                        onRefreshList={() => this.refreshTree(false)}
                        onPrevious={this.handleMoveToPrevious}
                        onNext={this.handleMoveToNext}
                    />
                }
                {state.showEditGroupDialog &&
                    <RecordFurtherEducationGroupDialog
                        id={state.editDialogId}
                        companyId={props.companyId}
                        canBeRemoved={state.selectedCanBeRemoved}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onSaved={this.handleEditDialogOk}
                        onClose={this.handleEditDialogCancel}
                        onRemove={this.removeGroup}
                        onRefreshList={() => this.refreshTree(false)}
                        onPrevious={this.handleMoveToPrevious}
                        onNext={this.handleMoveToNext}
                    />
                }
            </>
        );
    }
}